import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaEye, FaEyeSlash, FaInfoCircle, FaPlug, FaSyncAlt, FaWordpressSimple } from "react-icons/fa";
import StoreImporter, { Alert, Spinner } from "../StoreImport/StoreImporter";

const SOURCE = {
  name: "WooCommerce",
  Icon: FaWordpressSimple,
  api: "/api/admin/woocommerce-import",
  groups: "categories",
  subtitle: "Copy categories, products, variations, images, and SEO from a WordPress store into this catalog.",
  connectHint: "Store address and API keys",
  colors: { accent: "#7f54b3", dark: "#6b4499", light: "#a17bd4", soft: "rgba(127, 84, 179, 0.1)", ring: "rgba(127, 84, 179, 0.3)" },
};

const LABELS = { disconnected: "Not connected", connected: "Connected", error: "Connection failed" };

export default function WooCommerceStoreCloner() {
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connection, setConnection] = useState(null);
  const [storeUrl, setStoreUrl] = useState("");
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const applyStatus = (data) => {
    setConnection(data);
    if (data.store_domain) setStoreUrl(`https://${data.store_domain}`);
    if (data.auth_status === "error") setError(data.message || "");
  };

  useEffect(() => {
    document.title = "WooCommerce import — Admin";
    axios.get(`${SOURCE.api}/status`).then((response) => {
      applyStatus(response.data || {});
    }).catch(() => {
      setError("The store connection could not be loaded.");
    }).finally(() => setLoadingStatus(false));
  }, []);

  const testConnection = async () => {
    setError("");
    setNotice("");
    setConnecting(true);
    try {
      const payload = { store_url: storeUrl.trim() };
      if (consumerKey.trim()) payload.consumer_key = consumerKey.trim();
      if (consumerSecret.trim()) payload.consumer_secret = consumerSecret.trim();
      const response = await axios.post(`${SOURCE.api}/connect`, payload);
      applyStatus(response.data || {});
      setConsumerKey("");
      setConsumerSecret("");
      setShowSecret(false);
      setNotice(response.data.message || "Connected.");
    } catch (err) {
      const data = err.response?.data || {};
      if (data.auth_status) applyStatus(data);
      setError(data.message || "The store rejected the connection.");
    } finally {
      setConnecting(false);
    }
  };

  const status = connection?.auth_status || "disconnected";
  const ready = status === "connected";
  const label = ready && connection?.store_name ? `Connected · ${connection.store_name}` : (LABELS[status] || "Not connected");

  return (
    <StoreImporter
      source={SOURCE}
      connection={{ status: loadingStatus ? "loading" : status, ready, label }}
      connecting={connecting}
    >
      <div className="si-card">
        <div className="si-card-head">
          <h5>Source store</h5>
          {connection?.store_domain && !loadingStatus ? <span className="si-badge">{connection.store_domain}</span> : null}
        </div>
        <div className="si-card-body">
          {loadingStatus ? (
            <FormSkeleton />
          ) : (
            <>
              <div className="row g-3">
                <div className="col-lg-4">
                  <label className="form-label" htmlFor="woo-store">Store address</label>
                  <input id="woo-store" className="form-control" placeholder="https://your-store.com" value={storeUrl} onChange={(event) => setStoreUrl(event.target.value)} disabled={connecting} />
                </div>
                <div className="col-lg-4">
                  <label className="form-label" htmlFor="woo-key">Consumer key</label>
                  <input id="woo-key" className="form-control" placeholder={connection?.consumer_key ? `Saved (${connection.consumer_key})` : "ck_…"}
                    value={consumerKey} onChange={(event) => setConsumerKey(event.target.value)} autoComplete="off" disabled={connecting} />
                </div>
                <div className="col-lg-4">
                  <label className="form-label" htmlFor="woo-secret">Consumer secret</label>
                  <div className="si-input-wrap">
                    <input id="woo-secret" type={showSecret ? "text" : "password"} className="form-control"
                      placeholder={connection?.has_secret ? "Saved on the server" : "cs_…"}
                      value={consumerSecret} onChange={(event) => setConsumerSecret(event.target.value)} autoComplete="new-password" disabled={connecting} />
                    <button type="button" className="si-eye" onClick={() => setShowSecret(!showSecret)} aria-label={showSecret ? "Hide secret" : "Show secret"}>
                      {showSecret ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="si-help">
                <FaInfoCircle />
                <div>
                  In WordPress go to <strong>WooCommerce → Settings → Advanced → REST API</strong>, click <strong>Add key</strong>, choose <strong>Read</strong> permission, and generate it. The keys are stored encrypted on this server. The store must use https:// and permalinks must not be set to Plain.
                </div>
              </div>
              <div className="si-actions">
                <button type="button" className={`si-btn ${ready ? "" : "si-btn-dark"}`} onClick={testConnection} disabled={connecting}>
                  {connecting ? <Spinner /> : (ready ? <FaSyncAlt /> : <FaPlug />)}
                  {connecting ? "Connecting…" : (ready ? "Reconnect" : "Test Connection")}
                </button>
              </div>
            </>
          )}
          {notice ? <Alert tone="success" onClose={() => setNotice("")}>{notice}</Alert> : null}
          {error ? <Alert tone="error" onClose={() => setError("")}>{error}</Alert> : null}
        </div>
      </div>
    </StoreImporter>
  );
}

export function FormSkeleton() {
  return (
    <div className="row g-3" aria-busy="true" aria-label="Loading connection">
      {[0, 1, 2].map((item) => (
        <div className="col-lg-4" key={item}>
          <span className="si-skel mb-2" style={{ width: 110, height: 12 }} />
          <span className="si-skel" style={{ height: 42 }} />
        </div>
      ))}
      <div className="col-12">
        <span className="si-skel" style={{ height: 64 }} />
      </div>
    </div>
  );
}
